"use client"

import React, { useState } from "react"
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Collapse,
  Link,
  Tooltip,
  Drawer,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material"
import { Phone, LocationOn, ExpandMore, ExpandLess } from "@mui/icons-material"
import { fetchIncorrectProviderInfo } from "./providers-api"

const ProviderCard = ({ provider, userLocation, onViewDetails }) => {
  const [showMoreLocations, setShowMoreLocations] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedReasons, setSelectedReasons] = useState([])

  const toggleDrawer = (open) => () => setDrawerOpen(open)

  const handleReasonChange = (reason) => (event) => {
    if (event.target.checked) {
      setSelectedReasons((prev) => [...prev, reason])
    } else {
      setSelectedReasons((prev) => prev.filter((r) => r !== reason))
    }
  }

const handleSubmit = async () => {
  try {
    const result = await fetchIncorrectProviderInfo({
      providerNpi: provider.npi || provider.id,  
      incorrectData: selectedReasons,
    })

    setDrawerOpen(false)
    setSelectedReasons([])
  } catch (err) {
    console.error("Failed to submit incorrect provider info:", err)
  }
}

  const primaryLocation = provider.locations?.[0]
  const additionalLocations = provider.locations?.slice(1) || []

  const formatSpecialties = () => {
    // if (!provider.specialties || provider.specialties.length === 0) {
    //   return "Internal medicine"
    // }
    const names = provider.specialties.map((s) =>
      typeof s === "string" ? s : s.name
    )
    const full = names.join(" | ")
    if (full.length > 50 && names.length > 2) {
      return {
        visible: names.slice(0, 2).join(" | "),
        hasMore: true,
        remaining: names.slice(2).join(" | "),
        count: names.length - 2,
      }
    }
    return { visible: full, hasMore: false }
  }

  const specialtyInfo = formatSpecialties()

  const correctionOptions = [
    "Address is incorrect",
    "Phone number and/or fax number is incorrect",
    "Gender listed is incorrect",
    "Specialty is incorrect",
    "Doctor or group name is incorrect",
    "Doctor is deceased or retired",
    "This is a duplicate listing",
    "Doctor left this practice location",
  ]

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #bebebe",
          backgroundColor: "#ffffff",
          "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
        }}
      >
        <Box sx={{m: 2}}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Typography
              // component="button"
              // onClick={onViewDetails}
              sx={{
                color: "#111",
                // textDecoration: "underline",
                fontWeight: 500,
                fontSize: "1.125rem",
                border: "none",
                background: "none",
                cursor: "pointer",
                // p: 1,
                "&:hover": { color: "#111" },
              }}
            >
              {provider.name}
            </Typography>
            <Chip
              label="In - network"
              size="small"
              sx={{
                backgroundColor: "#dcfce7",
                color: "#16a34a",
                fontWeight: 500,
                fontSize: "0.75rem",
                height: "24px",
                borderRadius: "12px",
                border: "1px solid #bbf7d0",
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
          </Box>

          {/* Specialties */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
              {specialtyInfo.visible}
              {specialtyInfo.hasMore && (
                <Tooltip title={specialtyInfo.remaining} arrow placement="top">
                  <span
                    style={{
                      color: "#035fcb",
                      cursor: "pointer",
                      marginLeft: 4,
                      fontSize: "0.875rem",
                    }}
                  >
                    +{specialtyInfo.count} more
                  </span>
                </Tooltip>
              )}
            </Typography>
          </Box>

          {/* Report incorrect info + View details */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1, justifyContent: 'space-between' }}>
            <Link
              component="button"
              onClick={toggleDrawer(true)}
              sx={{
                color: "#666",
                textDecoration: "underline",
                fontSize: "0.875rem",
                fontWeight: 500,
                "&:hover": { color: "#111" },
              }}
            >
              Report incorrect information
            </Link>

            <Button
              variant="outlined"
              onClick={onViewDetails}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                color: "#035fcb",
                "&:hover": { color: "#035fcb", backgroundColor: "transparent" },
                p: 1,
                height: "24px",
              }}
            >
              View details
            </Button>
          </Box>
        </Box>

        {/* Primary Location */}
        <Box sx={{padding: 2, backgroundColor: 'aliceblue',borderRadius: 3, borderTopRightRadius: 0, borderTopLeftRadius: 3}}>
        {primaryLocation && (
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              p: 1.5,
              mb: additionalLocations.length ? 1 : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
              <LocationOn sx={{ fontSize: 20, color: "#9ca3af" }} />
              <Box>
                <Typography sx={{ color: "#111827", fontSize: "0.875rem", fontWeight: 500 }}>
                  {primaryLocation.address}
                </Typography>
              </Box>
            </Box>
            {primaryLocation.phone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Phone sx={{ fontSize: 20, color: "#9ca3af" }} />
                <Link
                  href={`tel:${primaryLocation.phone}`}
                  sx={{
                    color: "#035fcb",
                    textDecoration: "underline",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": { color: "#035fcb" },
                  }}
                >
                  {primaryLocation.phone}
                </Link>
              </Box>
            )}
          </Box>
        )}

        {/* Additional Locations */}
        {additionalLocations.length > 0 && (
          <>
            <Collapse in={showMoreLocations}>
              <Box>
                {additionalLocations.map((location, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: 2,
                      p: 1.5,
                      mb: index < additionalLocations.length - 1 ? 1.5 : 0,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: location.phone ? 1.5 : 0 }}>
                      <LocationOn sx={{ fontSize: 20, color: "#9ca3af" }} />
                      <Box>
                        <Typography sx={{ color: "#111827", fontSize: "0.875rem", fontWeight: 500 }}>
                          {location.address}
                        </Typography>
                      </Box>
                    </Box>
                    {location.phone && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Phone sx={{ fontSize: 20, color: "#9ca3af" }} />
                        <Link
                          href={`tel:${location.phone}`}
                          sx={{
                            color: "#035fcb",
                            textDecoration: "underline",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            "&:hover": { color: "#035fcb" },
                          }}
                        >
                          {location.phone}
                        </Link>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="text"
                onClick={() => setShowMoreLocations(!showMoreLocations)}
                endIcon={showMoreLocations ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  textTransform: "none",
                  color: "#035fcb",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  p: 0,
                  minHeight: "auto",
                  "&:hover": { backgroundColor: "transparent", color: "#1d4ed8" },
                }}
              >
                {additionalLocations.length} more location
                {additionalLocations.length > 1 ? "s" : ""} for this provider
              </Button>
            </Box>
          </>
        )}
        </Box>
      </Paper>

      {/* Drawer with scrollable content and bottom actions */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Scrollable area */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Typography variant="h3" sx={{ mb: 1, fontSize: '24px' }}>
            Thank you for your help
          </Typography>
          <Typography sx={{ mb: 1, fontWeight: 400 }}>
            Help us keep our information up to date. Use this form to let us know
            what corrections are needed and we’ll follow up with the doctor’s office.
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Reporting incorrect information for:
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>{provider.name}</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography sx={{ mb: 1, fontWeight: 400 }}>
            Which items need to be updated or corrected? (Select at least one) *
          </Typography>
          <FormGroup>
            {correctionOptions.map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    checked={selectedReasons.includes(opt)}
                    onChange={handleReasonChange(opt)}
                  />
                }
                label={opt}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Sticky bottom action buttons */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button variant="outlined" onClick={toggleDrawer(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={selectedReasons.length === 0}
            onClick={handleSubmit}
            sx={{ textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Drawer>
    </>
  )
}

export default ProviderCard
